import { type ReactNode, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface HeroProps {
  renderButton: (
    to: string,
    children: ReactNode,
    variant?: "primary" | "secondary" | "ghost",
  ) => JSX.Element;
}

export function Hero({ renderButton }: HeroProps) {
  const benefits = [
    {
      text: "maintain high standards",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      text: "catch issues early",
      gradient: "from-blue-400 to-pink-400",
    },
    {
      text: "avoid costly bugs",
      gradient: "from-pink-400 to-purple-400",
    },
    {
      text: "reduce technical debt",
      gradient: "from-pink-400 to-blue-400",
    },
    {
      text: "ship faster",
      gradient: "from-pink-400 to-pink-400",
    },
    {
      text: "lower costs",
      gradient: "from-pink-400 to-purple-400",
    },
    {
      text: "boost team productivity",
      gradient: "from-blue-400 to-purple-400",
    },
    {
      text: "improve code quality",
      gradient: "from-purple-400 to-pink-400",
    },
    {
      text: "accelerate development",
      gradient: "from-pink-400 to-blue-400",
    },
    {
      text: "reduce bottlenecks",
      gradient: "from-blue-400 to-purple-400",
    },
  ];

  const [currentBenefit, setCurrentBenefit] = useState(0);

  useEffect(() => {
    const benefitTimer = setInterval(() => {
      setCurrentBenefit((prev) => (prev + 1) % benefits.length);
    }, 3000);

    return () => {
      clearInterval(benefitTimer);
    };
  }, []);

  return (
    <section id="hero" className="relative overflow-hidden">
      {/* Aurora Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-[10px] opacity-30">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-[#8B5CF6] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora" />
          <div className="absolute bottom-0 -right-4 w-96 h-96 bg-[#3B82F6] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora delay-[2s]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#EC4899] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora delay-[4s]" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-white/90 to-white/80"
          >
            <span className="text-4xl md:text-6xl text-white/90 block">
              Open Source AI Code Review
            </span>
            
          </motion.h1>
          <h2>
          <span className="text-base md:text-xl mt-4 text-white/70 block">
              Premium Features & Customization Available
            </span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-xl md:text-2xl text-white/70 max-w-3xl mx-auto"
          >
            {" "}
            <motion.h2
              className="mt-4 text-2xl md:text-3xl text-white/80 aurora-gradient"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {/* <span className="text-purple-400">A</span>
              <span className="text-blue-400">C</span>
              <span className="text-pink-400">R</span>
              <span className="text-white/60 mx-2">|</span> */}
              AI Code Review
            </motion.h2>
            <h2 className="mt-4 text-2xl md:text-3xl text-white/80">
              that helps your team
            </h2>
            <AnimatePresence mode="wait">
              <motion.h2
                key={currentBenefit}
                initial={{
                  opacity: 0,
                  filter: "blur(8px)",
                  scale: 0.9,
                }}
                animate={{
                  opacity: 1,
                  filter: "blur(0px)",
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  filter: "blur(8px)",
                  scale: 1.1,
                }}
                transition={{
                  duration: 0.7,
                  ease: [0.76, 0, 0.24, 1],
                }}
                className={`inline-block bg-gradient-to-r ${benefits[currentBenefit].gradient} bg-clip-text text-transparent`}
              >
                {benefits[currentBenefit].text}
              </motion.h2>
            </AnimatePresence>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            {renderButton("/login", "Start Free")}
            {renderButton(
              "https://github.com/aicodereview/aicodereview",
              "Star on GitHub",
              "secondary",
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
